import { useState } from "react";
import {
  validateCounterInput,
  createDailyCounter,
  createGoalCounter,
  stampLastSelected,
} from "../utils/counterState";

export default function useCounterForm({
  counters,
  setCounters,
  setActiveCounterId,
  setGoalComplete,
  setIsRunning,
  setShowCreateCounter,
}) {
  // -------------------------
  // State
  // -------------------------
  const [activeTab, setActiveTab] = useState("daily");
  const [newCounterName, setNewCounterName] = useState("");
  const [newCounterLimit, setNewCounterLimit] = useState("");
  const [newCounterDays, setNewCounterDays] = useState("");
  const [editingCounterId, setEditingCounterId] = useState(null);
  const [formError, setFormError] = useState("");

  // -------------------------
  // Helpers
  // -------------------------
  const resetFormState = () => {
    setEditingCounterId(null);
    setNewCounterName("");
    setNewCounterLimit("");
    setNewCounterDays("");
    setFormError("");
  };

  const openCreateForm = () => {
    setShowCreateCounter(true);
    setFormError("");
    setIsRunning(false);
  };

  const closeCreateForm = () => {
    setShowCreateCounter(false);
    resetFormState();
    setIsRunning(true);
  };

  const handleEditActiveCounter = (counterId) => {
    const counter = counters.find((c) => c.id === counterId);
    if (!counter) return;

    setNewCounterName(counter.name);
    setNewCounterLimit(
      String(counter.limit || counter.perDayCount || "")
    );
    setNewCounterDays(String(counter.days || ""));
    setActiveTab(counter.type);
    setEditingCounterId(counter.id);
    setFormError("");

    setShowCreateCounter(true);
    setIsRunning(false);
  };

  // -------------------------
  // Core Logic
  // -------------------------
  const handleUpdateCounter = () => {
    if (!newCounterName.trim()) {
      setFormError("Please enter a counter name.");
      return false;
    }

    setCounters((prev) =>
      prev.map((c) =>
        c.id === editingCounterId
          ? { ...c, name: newCounterName }
          : c
      )
    );

    closeCreateForm();
    return true;
  };

  const handleCreateDaily = () => {
    const dailyCount = parseInt(newCounterLimit, 10);

    const error = validateCounterInput({
      type: "daily",
      name: newCounterName,
      limit: dailyCount,
    });

    if (error) {
      setFormError(error);
      return false;
    }

    const newCounter = createDailyCounter({
      name: newCounterName,
      dailyCount,
    });

    setCounters((prev) =>
      stampLastSelected([...prev, newCounter], newCounter.id)
    );

    setActiveCounterId(newCounter.id);
    setGoalComplete(false);
    closeCreateForm();

    return true;
  };

  const handleCreateGoal = () => {
    const limit = parseInt(newCounterLimit, 10);
    const days = parseInt(newCounterDays, 10);

    const error = validateCounterInput({
      type: "goal",
      name: newCounterName,
      limit,
      days,
    });

    if (error) {
      setFormError(error);
      return false;
    }

    const newCounter = createGoalCounter({
      name: newCounterName,
      limit,
      days,
    });

    setCounters((prev) =>
      stampLastSelected([...prev, newCounter], newCounter.id)
    );

    setActiveCounterId(newCounter.id);
    setGoalComplete(false);
    closeCreateForm();

    return true;
  };

  const handleCreateCounterSubmit = (e) => {
    e.preventDefault();
    setFormError("");

    if (editingCounterId) {
      return handleUpdateCounter();
    }

    if (activeTab === "daily") {
      return handleCreateDaily();
    }

    return handleCreateGoal();
  };

  // -------------------------
  // Exposed API
  // -------------------------
  return {
    activeTab,
    setActiveTab,
    newCounterName,
    setNewCounterName,
    newCounterLimit,
    setNewCounterLimit,
    newCounterDays,
    setNewCounterDays,
    editingCounterId,
    formError,
    setFormError,
    handleCreateCounterSubmit,
    handleEditActiveCounter,
    openCreateForm,
    closeCreateForm,
  };
}